import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { vitalSignsService } from '../services/vitalSignsService'
import { patientService } from '../services/patientService'
import {
  generateCentileCurveData,
  ageInMonthsFromDates,
  type Sex,
} from '../utils/ukWhoGrowthCharts'
import { Patient } from '../types/patient'
import { VitalSigns } from '../types/vitalSigns'
import { MaskedField } from './MaskedField'
import './GrowthCentileChart.css'

export type GrowthChartType = 'weight' | 'height' | 'bmi' | 'headCircumference'

interface GrowthCentileChartProps {
  patientId: string
  patient?: Patient
  chartType: GrowthChartType
  height?: number
}

const CENTILE_COLORS: Record<number, string> = {
  3: '#94a3b8',
  5: '#94a3b8',
  10: '#64748b',
  25: '#475569',
  50: '#334155',
  75: '#475569',
  90: '#64748b',
  95: '#94a3b8',
  97: '#94a3b8',
}

function getMeasurementValue(
  vs: VitalSigns,
  chartType: GrowthChartType,
): number | null {
  switch (chartType) {
    case 'weight':
      return vs.weight?.value ?? null
    case 'height':
      return vs.height?.value ?? null
    case 'bmi':
      return vs.bmi ?? null
    case 'headCircumference':
      return vs.headCircumference?.value ?? null
    default:
      return null
  }
}

function getYAxisLabel(chartType: GrowthChartType): string {
  switch (chartType) {
    case 'weight':
      return 'Weight (kg)'
    case 'height':
      return 'Height (cm)'
    case 'bmi':
      return 'BMI'
    case 'headCircumference':
      return 'Head circumference (cm)'
    default:
      return ''
  }
}

function getChartTitle(chartType: GrowthChartType): string {
  switch (chartType) {
    case 'weight':
      return 'Weight for age (WHO 2006)'
    case 'height':
      return 'Height for age (WHO 2006)'
    case 'bmi':
      return 'BMI for age (WHO 2006)'
    case 'headCircumference':
      return 'Head circumference for age (WHO 2006)'
    default:
      return ''
  }
}

export function GrowthCentileChart({
  patientId,
  patient: patientProp,
  chartType,
  height = 350,
}: GrowthCentileChartProps) {
  const { data: patientData } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getById(patientId),
    enabled: !!patientId && !patientProp,
  })

  const { data: vitalSigns, isLoading } = useQuery({
    queryKey: ['vital-signs', 'patient', patientId],
    queryFn: () => vitalSignsService.getByPatient(patientId),
    enabled: !!patientId,
  })

  const patient = patientProp ?? patientData

  const { chartData, patientPoints } = useMemo(() => {
    const sex: Sex | null =
      patient?.gender === 'male'
        ? 'male'
        : patient?.gender === 'female'
          ? 'female'
          : null

    if (!patient?.dateOfBirth || !sex) {
      return { chartData: [], patientPoints: [] }
    }

    const centileData = generateCentileCurveData(chartType, sex, 1)
    const points: Array<{ ageMonths: number; value: number; recordedAt: string }> = []

    if (vitalSigns) {
      for (const vs of vitalSigns) {
        const value = getMeasurementValue(vs, chartType)
        if (value == null || value <= 0) continue

        const recordedAt = vs.recordedAt || vs.createdAt
        const ageMonths = ageInMonthsFromDates(patient.dateOfBirth, recordedAt)
        if (ageMonths < 0 || ageMonths > 60) continue

        points.push({ ageMonths, value, recordedAt })
      }
    }

    // Merge patient points into chart data for correct Y domain and tooltips
    const mergedData = centileData.map((row) => {
      const pt = points.find((p) => p.ageMonths === row.ageMonths)
      return { ...row, patient: pt?.value }
    })

    return {
      chartData: mergedData,
      patientPoints: points.sort((a, b) => a.ageMonths - b.ageMonths),
    }
  }, [patient, vitalSigns, chartType])

  if (isLoading) {
    return (
      <div className="growth-centile-chart loading">
        Loading growth chart...
      </div>
    )
  }

  if (!patient?.dateOfBirth || !patient.gender || (patient.gender !== 'male' && patient.gender !== 'female')) {
    return (
      <div className="growth-centile-chart empty">
        Growth chart requires patient date of birth and sex.
      </div>
    )
  }

  return (
    <MaskedField
      field="vital-signs"
      value={vitalSigns}
      patientId={patientId}
      confidentialityLevel="restricted"
    >
      {(canAccess) => {
        if (!canAccess) {
          return (
            <div className="growth-centile-chart restricted">
              <p>You do not have permission to view growth charts.</p>
            </div>
          )
        }

        if (chartData.length === 0) {
          return (
            <div className="growth-centile-chart empty">
              No centile data available for this chart type.
            </div>
          )
        }

        const yAxisLabel = getYAxisLabel(chartType)

        return (
          <div className="growth-centile-chart">
            <div className="growth-centile-chart-header">
              <h3>{getChartTitle(chartType)}</h3>
            </div>
            <div className="growth-centile-chart-container">
              <ResponsiveContainer width="100%" height={height}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="ageMonths"
                    type="number"
                    domain={[0, 60]}
                    tickFormatter={(v) => `${Math.floor(v / 12)}y ${v % 12}m`}
                    label={{
                      value: 'Age',
                      position: 'insideBottom',
                      offset: -5,
                    }}
                  />
                  <YAxis
                    type="number"
                    domain={['auto', 'auto']}
                    label={{
                      value: yAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                    }}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      value != null && typeof value === 'number' ? value.toFixed(1) : String(value ?? ''),
                      name ?? '',
                    ]}
                    labelFormatter={(ageMonths) =>
                      `Age: ${Math.floor(Number(ageMonths) / 12)}y ${Number(ageMonths) % 12}m`
                    }
                  />
                  <Legend />

                  {/* Centile curves */}
                  <Line
                    type="monotone"
                    dataKey="p3"
                    stroke={CENTILE_COLORS[3]}
                    strokeWidth={1}
                    dot={false}
                    name="3rd"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p5"
                    stroke={CENTILE_COLORS[5]}
                    strokeWidth={1}
                    dot={false}
                    name="5th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p10"
                    stroke={CENTILE_COLORS[10]}
                    strokeWidth={1}
                    dot={false}
                    name="10th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p25"
                    stroke={CENTILE_COLORS[25]}
                    strokeWidth={1}
                    dot={false}
                    name="25th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p50"
                    stroke={CENTILE_COLORS[50]}
                    strokeWidth={1}
                    dot={false}
                    name="50th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p75"
                    stroke={CENTILE_COLORS[75]}
                    strokeWidth={1}
                    dot={false}
                    name="75th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p90"
                    stroke={CENTILE_COLORS[90]}
                    strokeWidth={1}
                    dot={false}
                    name="90th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p95"
                    stroke={CENTILE_COLORS[95]}
                    strokeWidth={1}
                    dot={false}
                    name="95th"
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="p97"
                    stroke={CENTILE_COLORS[97]}
                    strokeWidth={1}
                    dot={false}
                    name="97th"
                    connectNulls
                  />

                  {/* Patient data points */}
                  <Line
                    type="monotone"
                    dataKey="patient"
                    stroke="#0f172a"
                    strokeWidth={2}
                    dot={{ r: 5, fill: '#0f172a', stroke: '#fff', strokeWidth: 2 }}
                    connectNulls={false}
                    name="Patient"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {patientPoints.length > 0 && (
              <p className="growth-centile-chart-legend">
                Black dots: patient measurements. Reference: WHO 2006 Child Growth Standards.
              </p>
            )}
          </div>
        )
      }}
    </MaskedField>
  )
}
